import React, { useState, useEffect, useCallback } from 'react';
import { arBasisLine, isAllowedBasis } from '../utils/arBasis';
import './ARPage.css';
import { formatPatientName } from '../utils/formatName';
import { formatCurrency } from '../utils/formatCurrency';
import PatientARClaimsTable from '../components/PatientARClaimsTable';
import ClaimListActions from '../features/claimLists/ClaimListActions';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';
import useCapabilities from '../hooks/useCapabilities';
import StatementModal from '../components/StatementModal';
import ChargeCardAction from '../components/ChargeCardAction';
import { peekArTab, clearArLink } from '../features/reports/arLink';
import { openLedgerWith } from '../features/reports/ledgerLink';
import { formatPhoneDisplay } from '../utils/formatPhone';

const API_URL = process.env.REACT_APP_API_URL || '';

// en-CA's date format is YYYY-MM-DD, which matches the wire format.
function todayISO() {
  return new Date().toLocaleDateString('en-CA');
}

// The tab badges, always in thousands with one decimal so the two read alike
// ($0.4K, $8.2K, $41.0K, $1,203.5K) (David, 2026-10-21).
export function formatCompact(val) {
  const n = Number(val) || 0;
  return '$' + (n / 1000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K';
}

// The statuses the Insurance tab counts (weekly_summary_service.AR_CLAIM_STATUSES), in the
// ledger Status filter's own order.
const INSURANCE_AR_STATUSES = 'submitted,accepted,rejected,denied,partial,appealed';
// ar_closing's label for claims with no payer: no payer filter can select them.
const NO_PAYER = '(no payer recorded)';

// YYYY-MM-DD minus whole days, in UTC so a timezone never shifts the date.
function daysBefore(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/** The ledger filters for one Insurance amount: a payer (or every payer), a bucket (or
    every age), as of a date. The dates are the bucket's date-of-service window; `balance`
    keeps the claims the amount adds up. Exported for the test. */
export function insuranceLedgerFilters(asOf, bucketDays, payerName, bucket) {
  const [lo, hi] = (bucket && bucketDays && bucketDays[bucket]) || [0, null];
  return {
    status: INSURANCE_AR_STATUSES,
    payer: payerName ? [payerName] : [],
    date_from: hi == null ? '' : daysBefore(asOf, hi),
    date_to: daysBefore(asOf, lo),
    balance: 'insurance',
  };
}

function ARPage({ selectedPractice, readOnly = false, onOpenList }) {
  // Claims ticked in the patients' expanded rows, for "Create list" / "Add to list…".
  const [listSelectedIds, setListSelectedIds] = useState(() => new Set());
  const toggleListClaim = (claimId) => {
    setListSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else next.add(claimId);
      return next;
    });
  };
  // 'by_insurance' (payer × aging bucket, its own as-of date) | 'patients' (who owes
  // money). The Summary tab is gone (David, 2026-10-21). A dashboard A/R bar opens a
  // named tab (features/reports/arLink.js), read once here.
  const [activeTab, setActiveTab] = useState(
    () => peekArTab(selectedPractice && selectedPractice.id) || 'by_insurance',
  );
  // The hand-off is spent on mount, whichever practice it named.
  useEffect(() => {
    clearArLink();
  }, []);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Insurance tab state — GET /ar/by-payer-aging?as_of=. Fetched on load, like the
  // patient list, so the Insurance tab's badge always has its total.
  // Empty = no as_of sent; the backend ages to the practice's today.
  const [asOf, setAsOf] = useState(todayISO);
  const [agingData, setAgingData] = useState(null);
  const [agingLoading, setAgingLoading] = useState(false);
  const [agingError, setAgingError] = useState(null);

  // Patient tab state
  const [patientData, setPatientData] = useState([]);
  // The server stops at its row cap (largest balances first) and says so.
  const [patientTruncated, setPatientTruncated] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientSort, setPatientSort] = useState({ field: 'total_balance', order: 'desc' });
  const [expandedPatientId, setExpandedPatientId] = useState(null);
  const [patientClaims, setPatientClaims] = useState({});

  // Statement: the By patient row whose statement is open in the modal, if any.
  const [statementPatient, setStatementPatient] = useState(null);

  // Enrich state (replaces card sync)
  const [enriching, setEnriching] = useState(false);

  // Card filter: 'all' | 'yes' | 'no'
  const [cardFilter, setCardFilter] = useState('all');

  // Refund due: patients who overpaid (claims in credit), folded in from Collections
  // (David, 2026-10-25). A separate list, not a filter on the balances: these patients
  // owe nothing, so /ar/patients never returns them.
  const [refunds, setRefunds] = useState([]);
  const [showRefunds, setShowRefunds] = useState(false);

  // Billing state
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [showBillModal, setShowBillModal] = useState(false);
  const [billing, setBilling] = useState(false);
  const [billResults, setBillResults] = useState(null); // null | { total, succeeded, failed, total_charged, results }
  // The one By patient row a "Charge card" click chose. Kept as the row, not its
  // patient_id: /ar/patients groups by name and can hold two rows with the same
  // patient_id, and a by-id selection would charge that patient twice.
  const [chargeRow, setChargeRow] = useState(null);

  // Messages
  const [message, setMessage] = useState(null);

  const practiceId = selectedPractice?.id;
  // A list holds one practice's claims: a switch starts the selection over.
  useEffect(() => {
    setListSelectedIds(new Set());
  }, [practiceId]);

  // "Generate statement" needs the statements feature (routes/payments.py gates it).
  // "Charge card" reads card_charging, which Atlas switches on per practice.
  const capabilities = useCapabilities(practiceId);
  const statementsOn = !!capabilities.features.statements;
  const cardChargingOn = !!capabilities.features.card_charging;

  // Fetch patient data
  const fetchPatientData = useCallback(async () => {
    if (!practiceId) return;
    setPatientLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients?${params}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load patient A/R');
      const json = await resp.json();
      setPatientData(json.data || []);
      setPatientTruncated(!!json.truncated);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setPatientLoading(false);
    }
  }, [practiceId, dateFrom, dateTo]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  const fetchRefunds = useCallback(async () => {
    if (!practiceId || readOnly) return;
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients/refunds`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load refunds');
      const json = await resp.json();
      setRefunds(json.data || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [practiceId, readOnly]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  // Fetch the payer × bucket aging for the as-of date
  const fetchAgingData = useCallback(async () => {
    if (!practiceId) return;
    setAgingLoading(true);
    setAgingError(null);
    setAgingData(null);
    try {
      const params = new URLSearchParams();
      if (asOf) params.set('as_of', asOf);
      const query = params.toString();
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/by-payer-aging${query ? `?${query}` : ''}`, { credentials: 'include' });
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to load insurance aging');
      }
      const json = await resp.json();
      if (!json || !Array.isArray(json.buckets) || !Array.isArray(json.by_payer)) {
        throw new Error('The aging could not be read. Try again.');
      }
      setAgingData(json);
    } catch (err) {
      setAgingData(null);
      setAgingError(err.message);
    } finally {
      setAgingLoading(false);
    }
  }, [practiceId, asOf]);

  useEffect(() => {
    fetchAgingData();
  }, [fetchAgingData]);

  // Fetch claims for expanded patient row — uses patient_id if available, else patient_name
  const fetchPatientClaims = useCallback(async (patient) => {
    const key = patient.patient_name;
    if (!practiceId || patientClaims[key]) return;
    try {
      const params = new URLSearchParams();
      params.set('patient_name', patient.patient_name);
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients/claims?${params}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load claims');
      const json = await resp.json();
      setPatientClaims(prev => ({ ...prev, [key]: json.data || [] }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [practiceId, patientClaims]);

  // Toggle expanded row — keyed by patient_name
  const togglePatientRow = (patient) => {
    const key = patient.patient_name;
    if (expandedPatientId === key) {
      setExpandedPatientId(null);
    } else {
      setExpandedPatientId(key);
      fetchPatientClaims(patient);
    }
  };

  // Sorting
  const sortData = (data, sort) => {
    return [...data].sort((a, b) => {
      let aVal = a[sort.field];
      let bVal = b[sort.field];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sort.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handlePatientSort = (field) => {
    setPatientSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortArrow = (sort, field) => {
    if (sort.field !== field) return '';
    return sort.order === 'asc' ? ' ↑' : ' ↓';
  };

  // Enrich patients (phone, email, cards from EMR)
  const handleEnrich = async () => {
    if (!practiceId) return;
    setEnriching(true);
    setMessage(null);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/enrich-patients`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) {
        const json = await resp.json();
        throw new Error(json.error || 'Enrichment failed');
      }
      const json = await resp.json();
      const r = json.data;
      const parts = [`${r.enriched} updated`];
      if (r.resolved) parts.push(`${r.resolved} EMR IDs resolved`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      if (r.errors) parts.push(`${r.errors} errors`);
      setMessage({ type: 'success', text: `Enrichment complete: ${parts.join(', ')}.` });
      fetchPatientData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setEnriching(false);
    }
  };

  // Billing — checkbox helpers
  const billablePatients = patientData.filter(r => r.has_card_on_file && r.patient_id);
  const filteredPatients = patientData.filter(row =>
    cardFilter === 'all' ? true :
    cardFilter === 'yes' ? row.has_card_on_file :
    !row.has_card_on_file
  );

  const togglePatientSelection = (patientId) => {
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(patientId)) next.delete(patientId);
      else next.add(patientId);
      return next;
    });
  };

  const toggleAllBillable = () => {
    const visibleBillable = filteredPatients.filter(r => r.has_card_on_file && r.patient_id);
    const allSelected = visibleBillable.every(r => selectedPatients.has(r.patient_id));
    if (allSelected) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(visibleBillable.map(r => r.patient_id)));
    }
  };

  const selectedForBilling = chargeRow
    ? [chargeRow]
    : patientData.filter(r => selectedPatients.has(r.patient_id));
  const selectedTotal = selectedForBilling.reduce((sum, r) => sum + r.total_balance, 0);

  const handleBillSelected = async () => {
    if (!practiceId || selectedForBilling.length === 0) return;
    setBilling(true);
    setBillResults(null);
    setMessage(null);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/bill-patients`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patients: selectedForBilling.map(p => ({
            patient_id: p.patient_id,
            amount: p.total_balance,
          })),
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Billing failed');
      setBillResults(json);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setShowBillModal(false);
    } finally {
      setBilling(false);
    }
  };

  const closeBillModal = () => {
    setShowBillModal(false);
    setBillResults(null);
    setSelectedPatients(new Set());
    setChargeRow(null);
    fetchPatientData();
  };

  // Row action "Charge card": the same confirm-and-charge modal as Bill Selected,
  // with only this patient selected, so the one POST /ar/bill-patients flow runs.
  const chargeOne = (row) => {
    if (!row.patient_id) return;
    setBillResults(null);
    setChargeRow(row);
    setShowBillModal(true);
  };

  const patientTotals = patientData.reduce(
    (acc, row) => ({
      patients: acc.patients + 1,
      claims: acc.claims + row.claim_count,
      balance: acc.balance + row.total_balance,
    }),
    { patients: 0, claims: 0, balance: 0 }
  );

  // An Insurance amount opens Claims on the claims it adds up (David, 2026-10-21), the
  // same hand-off the dashboard's bars use. Zero, and the no-payer row (which no payer
  // filter can select), stay plain text.
  const amountLink = (amount, clickable, payerName, bucket) => {
    const text = formatCurrency(amount);
    if (!clickable || !(Number(amount) > 0) || !agingData || !agingData.as_of) return text;
    return (
      <button
        type="button"
        className="ar-amount-link"
        onClick={() => openLedgerWith(
          practiceId,
          insuranceLedgerFilters(agingData.as_of, agingData.bucket_days, payerName, bucket),
        )}
        title="Show these claims"
      >
        {text}
      </button>
    );
  };

  // A Patient balance opens Claims on that patient's claims with a balance.
  const openPatientClaims = (row) =>
    openLedgerWith(practiceId, {
      status: 'patient',
      patient: row.patient_name,
      date_from: dateFrom,
      date_to: dateTo,
      balance: 'patient',
    });

  if (!selectedPractice) {
    return <div className="ar-page"><p>Select a practice to view A/R.</p></div>;
  }

  return (
    <div className="ar-page">
      <div className="ar-header">
        <h1 className="page-title">Accounts Receivable</h1>
      </div>

      {/* Message */}
      {message && (
        <div className={`ar-message ${message.type}`}>
          {message.text}
          <button className="ar-message-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'by_insurance' ? 'active' : ''}`}
          onClick={() => setActiveTab('by_insurance')}
        >
          Insurance
          {agingData && agingData.total > 0 && (
            <span className="ar-tab-badge">{formatCompact(agingData.total)}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'patients' ? 'active' : ''}`}
          onClick={() => setActiveTab('patients')}
        >
          Patient
          {patientTotals.balance > 0 && (
            <span className="ar-tab-badge">{formatCompact(patientTotals.balance)}</span>
          )}
        </button>
      </div>

      {/* As-of date — By insurance ages balances to one day, not a service range */}
      {activeTab === 'by_insurance' && (
        <div className="ar-filter-bar">
          <label className="ar-filter-label" htmlFor="ar-aging-as-of">As of</label>
          <input
            id="ar-aging-as-of"
            type="date"
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            value={asOf}
            onChange={e => setAsOf(e.target.value)}
          />
          {asOf !== todayISO() && (
            <button className="btn btn-sm" onClick={() => setAsOf(todayISO())}>
              Today
            </button>
          )}
        </div>
      )}

      {/* Date Filters */}
      {activeTab !== 'by_insurance' && (
        <div className="ar-filter-bar">
          <label className="ar-filter-label">From</label>
          <input
            type="date"
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <label className="ar-filter-label">To</label>
          <input
            type="date"
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
          {(dateFrom || dateTo) && (
            <button className="btn btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>
              Clear Dates
            </button>
          )}
          {!readOnly && activeTab === 'patients' && (
            <>
              <div className="ar-card-filter">
                <button
                  className={`btn btn-sm ${!showRefunds && cardFilter === 'all' ? 'btn-primary' : ''}`}
                  onClick={() => { setCardFilter('all'); setShowRefunds(false); }}
                >All</button>
                <button
                  className={`btn btn-sm ${!showRefunds && cardFilter === 'yes' ? 'btn-primary' : ''}`}
                  onClick={() => { setCardFilter('yes'); setShowRefunds(false); }}
                >CC on File</button>
                <button
                  className={`btn btn-sm ${!showRefunds && cardFilter === 'no' ? 'btn-primary' : ''}`}
                  onClick={() => { setCardFilter('no'); setShowRefunds(false); }}
                >No CC</button>
                <button
                  className={`btn btn-sm ${showRefunds ? 'btn-primary' : ''}`}
                  onClick={() => setShowRefunds(true)}
                >Refund due ({refunds.length})</button>
              </div>
              <button
                className="btn btn-sm ar-sync-btn"
                onClick={handleEnrich}
                disabled={enriching}
                title="Fetch phone, email, and card data from EMR for patients missing info."
              >
                {enriching ? 'Enriching...' : 'Enrich'}
              </button>
              {selectedPatients.size > 0 && (
                <button
                  className="btn btn-sm btn-primary ar-bill-btn"
                  onClick={() => setShowBillModal(true)}
                >
                  Bill Selected ({selectedPatients.size}) — {formatCurrency(selectedTotal)}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* By insurance Tab — payer × aging bucket, mirrors ReportsPage ArClosing */}
      {activeTab === 'by_insurance' && (
        <div className="ar-tab-content">
          {agingLoading ? (
            <p className="ar-loading">Loading insurance aging...</p>
          ) : agingError ? (
            <p className="ar-empty ar-error">{agingError}</p>
          ) : !agingData ? null : agingData.by_payer.length === 0 ? (
            <p className="ar-empty">No outstanding insurance balances as of {agingData.as_of || asOf || 'today'}.</p>
          ) : (
            <div className="ar-table-wrapper">
              <table className="ar-table ar-aging-table">
                <thead>
                  {/* "Days Outstanding" spans the buckets: days since the date of service. */}
                  <tr className="ar-aging-group-row">
                    <th aria-hidden="true"></th>
                    <th colSpan={agingData.buckets.length} className="ar-aging-group">
                      Days Outstanding{isAllowedBasis(agingData.ar_basis) ? ' (at allowed)' : ''}
                    </th>
                    <th aria-hidden="true"></th>
                  </tr>
                  <tr>
                    <th>Payer</th>
                    {agingData.buckets.map(label => (
                      <th key={label} className="col-currency">{label.replace('-', '–')}</th>
                    ))}
                    <th className="col-currency">
                      {isAllowedBasis(agingData.ar_basis) ? 'Total at allowed' : 'Total'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {agingData.by_payer.map(row => {
                    const payer = row.payer_name === NO_PAYER ? null : row.payer_name;
                    return (
                      <tr key={row.payer_name}>
                        <td className="col-name">{row.payer_name}</td>
                        {agingData.buckets.map(label => (
                          <td key={label} className="col-currency">
                            {amountLink(row.buckets?.[label] ?? 0, payer != null, payer, label)}
                          </td>
                        ))}
                        <td className="col-currency col-outstanding">
                          {amountLink(row.total, payer != null, payer, null)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="ar-total-row">
                    <td className="col-name">Total</td>
                    {agingData.buckets.map(label => (
                      <td key={label} className="col-currency">
                        {amountLink(agingData.totals?.[label] ?? 0, true, null, label)}
                      </td>
                    ))}
                    <td className="col-currency col-outstanding">
                      {amountLink(agingData.total, true, null, null)}
                      {isAllowedBasis(agingData.ar_basis) && agingData.billed_total != null && (
                        <div className="ar-billed-beside" data-testid="ar-billed-total">
                          billed {formatCurrency(agingData.billed_total)}
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              {agingData.ar_basis && (
                <p className="ar-data-quality" data-testid="ar-basis-line">
                  {arBasisLine(agingData.ar_basis)}
                  {isAllowedBasis(agingData.ar_basis) &&
                    ' Patient A/R stays at billed; a click lists the claims at billed.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Patient Tab */}
      {activeTab === 'patients' && (
        <div className="ar-tab-content">
          {readOnly && !statementsOn && (
            <p className="ar-empty ar-statements-off">Statements are switched off for this practice — ask Atlas.</p>
          )}
          {!patientLoading && patientTruncated && (
            <p className="ar-truncated">
              Showing the largest patient balances only; the list was cut at the server's limit.
              Set a date range to narrow it.
            </p>
          )}
          {!showRefunds && (
            <ClaimListActions
              practiceId={practiceId}
              selectedIds={[...listSelectedIds]}
              source="manual"
              defaultTitle="A/R follow-up"
              onDone={() => setListSelectedIds(new Set())}
              onOpenList={onOpenList}
            />
          )}
          {showRefunds && !readOnly ? (
            refunds.length === 0 ? (
              <p className="ar-empty">No patients are owed a refund.</p>
            ) : (
              <div className="ar-table-wrapper">
                <table className="ar-table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th># Claims</th>
                      <th>Latest DOS</th>
                      <th className="col-currency">Refund due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.map((row) => (
                      <tr key={row.patient_id} className="ar-row">
                        <td className="col-name">{formatPatientName(row.patient_name)}</td>
                        <td>{row.claim_count}</td>
                        <td>{row.latest_dos || '-'}</td>
                        <td className="col-currency col-outstanding">{formatCurrency(row.refund_due)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : patientLoading ? (
            <p className="ar-loading">Loading patient A/R...</p>
          ) : patientData.length === 0 ? (
            <p className="ar-empty">No outstanding patient balances found.</p>
          ) : (
            <div className="ar-table-wrapper">
              <table className="ar-table">
                <thead>
                  <tr>
                    {!readOnly && (
                      <th className="col-checkbox" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={filteredPatients.filter(r => r.has_card_on_file && r.patient_id).length > 0 &&
                            filteredPatients.filter(r => r.has_card_on_file && r.patient_id).every(r => selectedPatients.has(r.patient_id))}
                          onChange={toggleAllBillable}
                          title="Select all billable patients"
                        />
                      </th>
                    )}
                    <th className="sortable" onClick={() => handlePatientSort('patient_name')}>
                      Patient{sortArrow(patientSort, 'patient_name')}
                    </th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th className="sortable" onClick={() => handlePatientSort('claim_count')}>
                      # Claims{sortArrow(patientSort, 'claim_count')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handlePatientSort('total_balance')}>
                      Balance{sortArrow(patientSort, 'total_balance')}
                    </th>
                    {!readOnly && <th>CC on File</th>}
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortData(filteredPatients, patientSort).map((row) => (
                    <React.Fragment key={row.patient_name}>
                      <tr
                        className={`ar-row ${expandedPatientId === row.patient_name ? 'expanded' : ''}`}
                        onClick={() => togglePatientRow(row)}
                      >
                        {!readOnly && (
                          <td className="col-checkbox" onClick={e => e.stopPropagation()}>
                            {row.has_card_on_file && row.patient_id ? (
                              <input
                                type="checkbox"
                                checked={selectedPatients.has(row.patient_id)}
                                onChange={() => togglePatientSelection(row.patient_id)}
                              />
                            ) : null}
                          </td>
                        )}
                        <td className="col-name">{formatPatientName(row.patient_name)}</td>
                        <td className="col-mono">{formatPhoneDisplay(row.phone) || '-'}</td>
                        <td className="col-mono">{row.email || '-'}</td>
                        <td>{row.claim_count}</td>
                        <td className="col-currency col-outstanding" onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            className="ar-amount-link"
                            onClick={() => openPatientClaims(row)}
                            title="Show these claims"
                          >
                            {formatCurrency(row.total_balance)}
                          </button>
                        </td>
                        {!readOnly && (
                          <td>
                            {row.has_card_on_file ? (
                              <span
                                className={`ar-card-badge ${row.last_decline ? 'ar-card-declined' : 'ar-card-yes'}`}
                                title={row.last_decline ? `Declined: ${row.last_decline.decline_reason || 'Unknown'} (${row.last_decline.declined_at ? new Date(row.last_decline.declined_at).toLocaleDateString() : ''})` : ''}
                              >
                                {row.cards?.[0] ? `${row.cards[0].card_type} ****${row.cards[0].last4}` : 'Yes'}
                              </span>
                            ) : (
                              <span className="ar-card-badge ar-card-no">No</span>
                            )}
                          </td>
                        )}
                        {/* Every role gets the statement. Charge card is staff-only for now;
                            the client view has Generate statement alone (David, 2026-10-21). */}
                        <td className="col-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setStatementPatient(row)}
                            disabled={!statementsOn || !row.patient_id}
                            title={
                              !statementsOn
                                ? 'Statements are switched off for this practice — ask Atlas'
                                : !row.patient_id
                                  ? 'No patient record matches this name, so there is no statement to generate'
                                  : 'Open this patient\'s statement to print or download'
                            }
                          >
                            Generate statement
                          </button>
                          {!readOnly && (
                            <ChargeCardAction
                              practiceId={practiceId}
                              patient={row}
                              cardChargingOn={cardChargingOn}
                              onCharge={chargeOne}
                            />
                          )}
                        </td>
                      </tr>
                      {expandedPatientId === row.patient_name && (
                        <tr className="ar-detail-row">
                          <td colSpan={readOnly ? 6 : 8}>
                            <div className="ar-detail-content">
                              <h4>Outstanding Claims</h4>
                              {!patientClaims[row.patient_name] ? (
                                <p className="ar-loading">Loading claims...</p>
                              ) : patientClaims[row.patient_name].length === 0 ? (
                                <p>No outstanding claims.</p>
                              ) : (
                                <PatientARClaimsTable
                                  claims={patientClaims[row.patient_name]}
                                  selectedIds={listSelectedIds}
                                  onToggle={toggleListClaim}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Statement preview — print or download, sends nothing */}
      {statementPatient && (
        <StatementModal
          practiceId={practiceId}
          patientId={statementPatient.patient_id}
          patientName={statementPatient.patient_name}
          onClose={() => setStatementPatient(null)}
        />
      )}

      {/* Billing Confirmation / Results Modal. Opened by Bill Selected (staff) or a
          row's Charge card (every role); the backend gates the charge itself. */}
      {showBillModal && (
        <div className="modal-overlay" onClick={() => { if (!billing) closeBillModal(); }}>
          <div className="modal-content ar-bill-modal" onClick={e => e.stopPropagation()}>
            {!billResults ? (
              <>
                <h2>Confirm Billing</h2>
                <p className="ar-bill-summary">
                  Charge <strong>{selectedForBilling.length}</strong> patient{selectedForBilling.length !== 1 ? 's' : ''} for
                  a total of <strong>{formatCurrency(selectedTotal)}</strong>
                </p>
                <div className="ar-bill-table-wrapper">
                  <table className="ar-bill-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th className="col-currency">Balance</th>
                        <th>Card</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedForBilling.map(p => (
                        <tr key={p.patient_id}>
                          <td className="col-name">{formatPatientName(p.patient_name)}</td>
                          <td className="col-currency col-outstanding">{formatCurrency(p.total_balance)}</td>
                          <td>
                            {p.cards?.[0]
                              ? `${p.cards[0].card_type} ****${p.cards[0].last4}`
                              : 'Card on file'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-actions">
                  <button className="btn" onClick={closeBillModal} disabled={billing}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleBillSelected}
                    disabled={billing}
                  >
                    {billing ? 'Processing...' : `Confirm & Charge ${formatCurrency(selectedTotal)}`}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Billing Results</h2>
                <div className="ar-bill-result-summary">
                  <div className={`ar-bill-stat ${billResults.succeeded > 0 ? 'success' : ''}`}>
                    <span className="ar-bill-stat-num">{billResults.succeeded}</span>
                    <span className="ar-bill-stat-label">Charged</span>
                  </div>
                  {billResults.failed > 0 && (
                    <div className="ar-bill-stat error">
                      <span className="ar-bill-stat-num">{billResults.failed}</span>
                      <span className="ar-bill-stat-label">Failed</span>
                    </div>
                  )}
                  <div className="ar-bill-stat">
                    <span className="ar-bill-stat-num">{formatCurrency(billResults.total_charged)}</span>
                    <span className="ar-bill-stat-label">Total Charged</span>
                  </div>
                </div>
                <div className="ar-bill-table-wrapper">
                  <table className="ar-bill-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th className="col-currency">Amount</th>
                        <th>Card</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billResults.results.map((r, i) => (
                        <tr key={i} className={r.success ? '' : r.declined ? 'ar-bill-row-declined' : 'ar-bill-row-failed'}>
                          <td className="col-name">{formatPatientName(r.patient_name)}</td>
                          <td className="col-currency">{formatCurrency(r.amount_charged || r.amount)}</td>
                          <td>{r.card_type && r.last4 ? `${r.card_type} ****${r.last4}` : '-'}</td>
                          <td>
                            {r.success ? (
                              <span className="ar-bill-status-ok">Charged</span>
                            ) : r.declined ? (
                              <span className="ar-bill-status-declined" title={r.decline_reason}>{r.decline_reason || 'Card Declined'}</span>
                            ) : (
                              <span className="ar-bill-status-fail" title={r.error}>{r.error || 'Failed'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={closeBillModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default ARPage;
